// Combobox-based version picker for the upstream chromium catalog.
//
// Uses the popup pattern: a Button trigger displays the current
// selection, and clicking it opens a popover with a search input + a
// scrollable, grouped (by major) list. Versions already published in
// the manifest are rendered disabled with an "already published" chip
// so the operator never accidentally re-runs a duplicate publish.

import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import {
	Combobox,
	ComboboxCollection,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxGroup,
	ComboboxInput,
	ComboboxItem,
	ComboboxLabel,
	ComboboxList,
	ComboboxTrigger,
} from "@veil/ui/components/combobox";
import { InputGroupAddon } from "@veil/ui/components/input-group";
import { formatRelative } from "@veil/ui/lib/format";
import { SearchIcon } from "lucide-react";
import { useMemo } from "react";
import type { UpstreamRelease } from "@/lib/releases/types.ts";

interface Props {
	id?: string;
	releases: UpstreamRelease[];
	publishedSet: Set<string>;
	value: string | undefined;
	onChange: (next: string) => void;
	disabled?: boolean;
}

interface Group {
	major: number;
	rows: UpstreamRelease[];
}

export function CatalogVersionSelect({
	id,
	releases,
	publishedSet,
	value,
	onChange,
	disabled,
}: Props) {
	const grouped = useMemo<Group[]>(() => {
		const map = new Map<number, UpstreamRelease[]>();
		for (const r of releases) {
			if (!r.hasX64Deb) continue;
			if (!map.has(r.browserMajor)) map.set(r.browserMajor, []);
			map.get(r.browserMajor)?.push(r);
		}
		return Array.from(map.entries())
			.map(([major, rows]) => ({
				major,
				rows: rows
					.slice()
					.sort(
						(a, b) =>
							new Date(b.publishedAt).getTime() -
							new Date(a.publishedAt).getTime(),
					),
			}))
			.sort((a, b) => b.major - a.major);
	}, [releases]);

	const selected = useMemo(
		() => releases.find((r) => r.fullVersion === value) ?? null,
		[releases, value],
	);
	const selectedAlreadyPublished =
		!!selected && publishedSet.has(selected.fullVersion);

	return (
		<Combobox
			items={grouped}
			value={value ?? null}
			onValueChange={(v: string | null) => {
				if (v) onChange(v);
			}}
			disabled={disabled}
		>
			<ComboboxTrigger
				id={id}
				render={
					<Button
						type="button"
						variant="outline"
						className="w-full justify-between font-normal"
					/>
				}
			>
				{selected ? (
					<span className="flex min-w-0 items-center gap-2">
						<span className="font-mono text-xs tabular-nums">
							{selected.fullVersion}
						</span>
						<span className="truncate text-[10px] text-muted-foreground">
							v{selected.browserMajor} · {formatRelative(selected.publishedAt)}
						</span>
						{selectedAlreadyPublished ? (
							<Badge variant="secondary" className="text-[10px]">
								already published
							</Badge>
						) : null}
					</span>
				) : (
					<span className="text-muted-foreground">Pick a version…</span>
				)}
			</ComboboxTrigger>
			<ComboboxContent className="max-h-[60vh]">
				<ComboboxInput showTrigger={false} placeholder="Search versions…">
					<InputGroupAddon>
						<SearchIcon />
					</InputGroupAddon>
				</ComboboxInput>
				<ComboboxEmpty>No versions match.</ComboboxEmpty>
				<ComboboxList>
					{(group: Group) => (
						<ComboboxGroup key={group.major} items={group.rows}>
							<ComboboxLabel>Chromium v{group.major}</ComboboxLabel>
							<ComboboxCollection>
								{(r: UpstreamRelease) => {
									const alreadyPublished = publishedSet.has(r.fullVersion);
									return (
										<ComboboxItem
											key={r.tagName}
											value={r.fullVersion}
											disabled={alreadyPublished}
										>
											<span className="font-mono text-xs tabular-nums">
												{r.fullVersion}
											</span>
											<span className="text-[10px] text-muted-foreground">
												{formatRelative(r.publishedAt)}
											</span>
											{alreadyPublished ? (
												<Badge
													variant="secondary"
													className="ml-auto text-[10px]"
												>
													already published
												</Badge>
											) : null}
										</ComboboxItem>
									);
								}}
							</ComboboxCollection>
						</ComboboxGroup>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
}
